import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Download, Check, Loader2, BookOpen, Headphones, Gift, ArrowLeft, Copy, Users, DollarSign, MousePointerClick, UserPlus } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Amazon-ready covers
const AMAZON_COVERS = {
  1: "https://static.prod-images.emergentagent.com/jobs/e478d2d4-8d2a-4f20-b187-0af8d00cf009/images/93b037ec6f083e7b25c51a57a7c9bb283444ae93dd6092cb69029544daf3e705.png",
  2: "https://static.prod-images.emergentagent.com/jobs/e478d2d4-8d2a-4f20-b187-0af8d00cf009/images/a1a7976a4ce937ac69f68cfd8c1fb2e9d54b7640b1f3390f6acced30c6b29955.png",
  3: "https://static.prod-images.emergentagent.com/jobs/e478d2d4-8d2a-4f20-b187-0af8d00cf009/images/7b9bc766203211ea6562153ab2187ad5fc931fafeac12dae3ae78f593102f0ed.png"
};

function ReferralSection() {
  const [refEmail, setRefEmail] = useState("");
  const [referralCode, setReferralCode] = useState(null);
  const [stats, setStats] = useState(null);
  const [creating, setCreating] = useState(false);
  const [lookupEmail, setLookupEmail] = useState("");
  const [lookingUp, setLookingUp] = useState(false);

  const createReferral = async (e) => {
    e.preventDefault();
    if (!refEmail) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/referrals/create?email=${encodeURIComponent(refEmail)}`);
      setReferralCode(res.data.code);
      toast.success("Referral link created!");
      // Fetch stats immediately
      const statsRes = await axios.get(`${API}/referrals/stats/${res.data.code}`);
      setStats(statsRes.data);
    } catch (err) {
      toast.error("Failed to create referral link.");
    } finally {
      setCreating(false);
    }
  };

  const lookupReferral = async (e) => {
    e.preventDefault();
    if (!lookupEmail) return;
    setLookingUp(true);
    try {
      const res = await axios.get(`${API}/referrals/lookup?email=${encodeURIComponent(lookupEmail)}`);
      setReferralCode(res.data.code);
      setStats(res.data);
      toast.success("Referral found!");
    } catch (err) {
      toast.error("No referral found for this email.");
    } finally {
      setLookingUp(false);
    }
  };

  const referralLink = referralCode ? `${window.location.origin}/store?ref=${referralCode}` : "";

  const copyLink = async () => {
    if (!referralLink) return;
    await navigator.clipboard.writeText(referralLink);
    toast.success("Referral link copied!");
  };

  return (
    <section data-testid="referral-section" className="border-t border-[#262626] pt-12">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-signal-red/10 border border-signal-red/30 rounded-full mb-4">
          <Users className="w-3.5 h-3.5 text-signal-red" />
          <span className="text-xs font-mono text-signal-red tracking-wider">REFERRAL PROGRAM</span>
        </div>
        <h3 className="text-2xl md:text-3xl font-['Syne'] font-bold text-[#e5e5e5] mb-2">
          Earn <span className="text-terminal-green">15%</span> on Every Sale
        </h3>
        <p className="text-sm text-[#a3a3a3] max-w-lg mx-auto">
          Share your unique link. When someone purchases through it, you earn 15% commission on every sale.
        </p>
      </div>

      <div className="max-w-xl mx-auto space-y-6">
        {!referralCode ? (
          <div className="space-y-4">
            {/* Create new referral */}
            <form onSubmit={createReferral} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={refEmail}
                onChange={(e) => setRefEmail(e.target.value)}
                placeholder="Your email to start earning"
                data-testid="referral-email-input"
                className="flex-1 px-4 py-3 bg-[#141414] border border-[#262626] focus:border-terminal-green outline-none font-mono text-sm text-[#e5e5e5]"
                required
              />
              <button
                type="submit"
                disabled={creating}
                data-testid="create-referral-btn"
                className="px-6 py-3 bg-terminal-green text-[#0a0a0a] font-mono text-sm font-bold hover:bg-green-400 transition-colors disabled:opacity-50"
              >
                {creating ? "..." : "Get My Link"}
              </button>
            </form>

            {/* Lookup existing */}
            <div className="text-center">
              <p className="text-xs text-[#525252] mb-2">Already have a referral? Look it up:</p>
              <form onSubmit={lookupReferral} className="flex gap-2 max-w-sm mx-auto">
                <input
                  type="email"
                  value={lookupEmail}
                  onChange={(e) => setLookupEmail(e.target.value)}
                  placeholder="Your email"
                  data-testid="referral-lookup-input"
                  className="flex-1 px-3 py-2 bg-[#141414] border border-[#262626] focus:border-signal-red outline-none font-mono text-xs text-[#e5e5e5]"
                  required
                />
                <button
                  type="submit"
                  disabled={lookingUp}
                  data-testid="referral-lookup-btn"
                  className="px-4 py-2 border border-[#262626] hover:border-signal-red text-[#a3a3a3] hover:text-signal-red font-mono text-xs transition-colors"
                >
                  {lookingUp ? "..." : "Look Up"}
                </button>
              </form>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Referral link display */}
            <div className="bg-[#141414] border border-terminal-green/40 p-4">
              <p className="text-xs text-terminal-green font-mono mb-2">YOUR REFERRAL LINK</p>
              <div className="flex items-center gap-2">
                <code data-testid="referral-link-display" className="flex-1 text-sm text-[#e5e5e5] font-mono bg-[#0a0a0a] p-3 border border-[#262626] truncate">
                  {referralLink}
                </code>
                <button
                  onClick={copyLink}
                  data-testid="copy-referral-link-btn"
                  className="p-3 bg-terminal-green text-[#0a0a0a] hover:bg-green-400 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Stats grid */}
            {stats && (
              <div data-testid="referral-stats" className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-[#141414] border border-[#262626] p-4 text-center">
                  <MousePointerClick className="w-4 h-4 text-[#525252] mx-auto mb-1" />
                  <p className="text-xl font-bold text-[#e5e5e5] font-mono">{stats.clicks}</p>
                  <p className="text-[10px] text-[#525252] font-mono">CLICKS</p>
                </div>
                <div className="bg-[#141414] border border-[#262626] p-4 text-center">
                  <UserPlus className="w-4 h-4 text-[#525252] mx-auto mb-1" />
                  <p className="text-xl font-bold text-[#e5e5e5] font-mono">{stats.signups}</p>
                  <p className="text-[10px] text-[#525252] font-mono">SIGNUPS</p>
                </div>
                <div className="bg-[#141414] border border-[#262626] p-4 text-center">
                  <ShoppingCart className="w-4 h-4 text-[#525252] mx-auto mb-1" />
                  <p className="text-xl font-bold text-[#e5e5e5] font-mono">{stats.purchases}</p>
                  <p className="text-[10px] text-[#525252] font-mono">PURCHASES</p>
                </div>
                <div className="bg-[#141414] border border-terminal-green/40 p-4 text-center">
                  <DollarSign className="w-4 h-4 text-terminal-green mx-auto mb-1" />
                  <p className="text-xl font-bold text-terminal-green font-mono">${stats.commission_earned?.toFixed(2) || "0.00"}</p>
                  <p className="text-[10px] text-[#525252] font-mono">EARNED</p>
                </div>
              </div>
            )}

            <button
              onClick={() => { setReferralCode(null); setStats(null); setRefEmail(""); setLookupEmail(""); }}
              className="text-xs text-[#525252] hover:text-[#a3a3a3] font-mono transition-colors"
            >
              Use a different email
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export default function StorePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  // Track referral code from URL
  useEffect(() => {
    const refCode = searchParams.get("ref");
    if (refCode) {
      localStorage.setItem("referral_code", refCode.toUpperCase());
      // Track click
      axios.post(`${API}/referrals/${refCode}/click`).catch(() => {});
    }
  }, [searchParams]);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data.products);
      setLoading(false);
    } catch (error) {
      // Error fetching products
      toast.error("Failed to load products");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handlePurchase = async (productId) => {
    setPurchasing(productId);
    try {
      const referralCode = localStorage.getItem("referral_code") || null;
      const response = await axios.post(`${API}/checkout/create`, {
        product_id: productId,
        origin_url: window.location.origin,
        referral_code: referralCode
      });

      if (response.data.free) {
        toast.success("Book 1 is free! Redirecting to download...");
        navigate(`/purchase/success?session_id=free_${response.data.download_token}`);
      } else {
        window.location.href = response.data.url;
      }
    } catch (error) {
      // Error creating checkout
      toast.error("Failed to start checkout. Please try again.");
    } finally {
      setPurchasing(null);
    }
  };

  const getProductIcon = (type) => {
    switch (type) {
      case "ebook": return <BookOpen className="w-5 h-5" />;
      case "bundle": return <Gift className="w-5 h-5" />;
      case "premium": return <Headphones className="w-5 h-5" />;
      default: return <BookOpen className="w-5 h-5" />;
    }
  };

  const getProductCover = (product) => {
    if (product.books_included?.length === 1) {
      return AMAZON_COVERS[product.books_included[0]];
    }
    return AMAZON_COVERS[1];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-terminal-green" />
      </div>
    );
  }

  const individualBooks = products.filter(p => p.type === "ebook");
  const bundles = products.filter(p => p.type !== "ebook");

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <div className="border-b border-[#262626] p-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            data-testid="store-back-btn"
            className="flex items-center gap-2 text-[#a3a3a3] hover:text-[#e5e5e5] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-mono text-sm">Back</span>
          </button>
          <h1 className="font-['Syne'] font-bold text-xl text-[#e5e5e5]">
            <span className="text-signal-red">Store</span>
          </h1>
          <div className="w-20" />
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-12">
        {/* Hero */}
        <div className="text-center space-y-4">
          <h2 className="text-3xl md:text-4xl font-['Syne'] font-bold text-[#e5e5e5]">
            Get <span className="text-signal-red">The Human Dominion</span>
          </h2>
          <p className="text-[#a3a3a3] max-w-2xl mx-auto">
            Purchase directly and keep 97% more of your money supporting the author. 
            All files are DRM-free and yours forever.
          </p>
        </div>

        {/* Bundle Deals */}
        <section>
          <h3 className="font-['Syne'] font-bold text-xl text-terminal-green mb-6 flex items-center gap-2">
            <Gift className="w-5 h-5" />
            Best Value Bundles
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {bundles.map((product) => (
              <div
                key={product.id}
                className="bg-[#141414] border-2 border-terminal-green p-6 relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 bg-terminal-green text-[#0a0a0a] px-3 py-1 text-xs font-mono font-bold">
                  SAVE {product.type === "premium" ? "40%" : "33%"}
                </div>
                
                <div className="flex gap-6">
                  <div className="flex -space-x-4">
                    {product.books_included?.slice(0, 3).map((bookNum, i) => (
                      <div 
                        key={bookNum}
                        className="w-16 h-24 border border-[#262626] overflow-hidden"
                        style={{ zIndex: 3 - i }}
                      >
                        <img 
                          src={AMAZON_COVERS[bookNum]} 
                          alt={`Book ${bookNum}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getProductIcon(product.type)}
                      <h4 className="font-['Syne'] font-bold text-lg text-[#e5e5e5]">
                        {product.name}
                      </h4>
                    </div>
                    <p className="text-sm text-[#a3a3a3] mb-4">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-2xl font-bold text-terminal-green">
                          ${product.price.toFixed(2)}
                        </span>
                        {product.type === "bundle" && (
                          <span className="text-sm text-[#525252] line-through ml-2">$14.97</span>
                        )}
                        {product.type === "premium" && (
                          <span className="text-sm text-[#525252] line-through ml-2">$24.97</span>
                        )}
                      </div>
                      <button
                        onClick={() => handlePurchase(product.id)}
                        disabled={purchasing === product.id}
                        data-testid={`buy-${product.id}`}
                        className="px-6 py-2 bg-terminal-green text-[#0a0a0a] font-mono text-sm font-bold hover:bg-green-400 transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        {purchasing === product.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <ShoppingCart className="w-4 h-4" />
                        )}
                        Buy Now
                      </button>
                    </div>
                  </div>
                </div>
                
                {product.includes_audio && (
                  <div className="mt-4 pt-4 border-t border-[#262626] flex items-center gap-2 text-sm text-signal-red">
                    <Headphones className="w-4 h-4" />
                    Includes audiobook access
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Individual Books */}
        <section>
          <h3 className="font-['Syne'] font-bold text-xl text-[#e5e5e5] mb-6 flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Individual Books
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {individualBooks.map((product) => (
              <div
                key={product.id}
                className={`bg-[#141414] border p-4 ${
                  product.price === 0 ? 'border-terminal-green' : 'border-[#262626]'
                }`}
              >
                <div className="aspect-[2/3] mb-4 overflow-hidden border border-[#262626]">
                  <img 
                    src={getProductCover(product)} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <h4 className="font-['Syne'] font-bold text-[#e5e5e5] mb-1">
                  {product.name}
                </h4>
                <p className="text-xs text-[#a3a3a3] mb-4">
                  {product.description}
                </p>
                
                <div className="flex items-center justify-between">
                  {product.price === 0 ? (
                    <span className="px-3 py-1 bg-terminal-green text-[#0a0a0a] text-sm font-mono font-bold">
                      FREE
                    </span>
                  ) : (
                    <span className="text-xl font-bold text-[#e5e5e5]">
                      ${product.price.toFixed(2)}
                    </span>
                  )}
                  <button
                    onClick={() => handlePurchase(product.id)}
                    disabled={purchasing === product.id}
                    data-testid={`buy-${product.id}`}
                    className={`px-4 py-2 font-mono text-sm font-bold transition-colors disabled:opacity-50 flex items-center gap-2 ${
                      product.price === 0
                        ? 'bg-terminal-green text-[#0a0a0a] hover:bg-green-400'
                        : 'bg-signal-red text-white hover:bg-red-700'
                    }`}
                  >
                    {purchasing === product.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : product.price === 0 ? (
                      <>
                        <Download className="w-4 h-4" />
                        Get Free
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-4 h-4" />
                        Buy
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Trust badges */}
        <div className="border-t border-[#262626] pt-8">
          <div className="flex flex-wrap justify-center gap-8 text-center">
            <div>
              <Check className="w-6 h-6 text-terminal-green mx-auto mb-2" />
              <div className="text-sm text-[#a3a3a3]">Secure Payment</div>
            </div>
            <div>
              <Download className="w-6 h-6 text-terminal-green mx-auto mb-2" />
              <div className="text-sm text-[#a3a3a3]">Instant Download</div>
            </div>
            <div>
              <BookOpen className="w-6 h-6 text-terminal-green mx-auto mb-2" />
              <div className="text-sm text-[#a3a3a3]">DRM-Free Files</div>
            </div>
          </div>
        </div>

        {/* Referral Program */}
        <ReferralSection />
      </div>
    </div>
  );
}
